/**
 * Service for real-time conversation updates via Server-Sent Events.
 *
 * Read with fetch, not EventSource, so the token travels in the Authorization header
 * (never in a URL or an access log) and every reconnect can carry a freshly minted one.
 */

import { getAuthToken, refreshUserToken } from './firebase';
import { logger } from '../utils/logger';
import { getImpersonationUserId } from '../stores/impersonationStore';

// The server writes a heartbeat every 30 s (conversationUpdatesController).
const STALE_AFTER_MS = 45_000;
const SILENT_LIMIT_MS = 75_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

interface ConversationUpdateEvent {
  type: 'conversation_updated' | 'connected' | 'heartbeat' | 'error';
  conversationId?: string;
  updates?: {
    projectTitle?: string;
    projectThumbnail?: string;
    [key: string]: any;
  };
  timestamp: string;
  connectionId?: string;
  message?: string;
}

type ConversationUpdateCallback = (conversationId: string, updates: any) => void;

// Audio event types for real-time audio generation updates
export interface AudioEvent {
  type: 'audio_status' | 'audio_generated' | 'audio_error';
  slideId: string;
  slideIdentifier: string;
  status?: string;
  progress?: number;
  message?: string;
  audioUrl?: string;
  audioDuration?: number;
  wordTimings?: any[];
  error?: string;
}

type AudioEventCallback = (conversationId: string, audioEvent: AudioEvent) => void;

// Transcription event types for audio upload transcription
export interface TranscriptionEvent {
  type: 'transcription_status' | 'transcription_complete' | 'transcription_error';
  resourceId: string;
  status?: string;
  message?: string;
  transcript?: string;
  wordTimings?: Array<{ word: string; startTime: number; endTime: number; confidence: number }>;
  duration?: number;
  wordCount?: number;
  error?: string;
}

type TranscriptionEventCallback = (conversationId: string, event: TranscriptionEvent) => void;

class ConversationUpdatesService {
  private stream: AbortController | null = null;
  private open = false;
  private lastHeardAt = 0;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private callbacks: Set<ConversationUpdateCallback> = new Set();
  private audioCallbacks: Set<AudioEventCallback> = new Set();
  private transcriptionCallbacks: Set<TranscriptionEventCallback> = new Set();
  private currentTeamId: string | null = null;
  // The user an admin is viewing as (View As) when this stream started; the server opens that user's stream.
  private viewingAs: string | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;

  /**
   * Start listening for conversation updates for a specific team
   */
  startListening(teamId: string): void {
    const viewingAs = getImpersonationUserId();
    if (this.currentTeamId === teamId && this.viewingAs === viewingAs) {
      logger.debug('🔔 Already listening for conversation updates for team:', teamId);
      return;
    }

    if (this.currentTeamId) {
      this.stopListening();
    }

    this.currentTeamId = teamId;
    this.viewingAs = viewingAs;
    this.reconnectAttempts = 0;
    window.addEventListener('online', this.onWake);
    document.addEventListener('visibilitychange', this.onVisibility);
    void this._connect(false);
  }

  /**
   * Stop listening for conversation updates
   */
  stopListening(): void {
    if (this.stream) {
      logger.info('🔌 Stopping conversation updates listener');
    }
    this._closeStream();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    window.removeEventListener('online', this.onWake);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.currentTeamId = null;
    this.viewingAs = null;
    this.reconnectAttempts = 0;
  }

  /**
   * Add a callback for conversation updates
   */
  onConversationUpdate(callback: ConversationUpdateCallback): () => void {
    this.callbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
    };
  }

  /**
   * Add a callback for audio events (generation status, completion, errors)
   */
  onAudioEvent(callback: AudioEventCallback): () => void {
    this.audioCallbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.audioCallbacks.delete(callback);
    };
  }

  /**
   * Add a callback for transcription events (status, completion, errors)
   */
  onTranscriptionEvent(callback: TranscriptionEventCallback): () => void {
    this.transcriptionCallbacks.add(callback);
    return () => {
      this.transcriptionCallbacks.delete(callback);
    };
  }

  /**
   * Get current connection status
   */
  isConnected(): boolean {
    return this.open;
  }

  /**
   * Get current team ID being listened to
   */
  getCurrentTeamId(): string | null {
    return this.currentTeamId;
  }

  // The network came back or the tab was shown: a laptop that slept may hold a dead
  // connection that has not errored yet, so a stream that went quiet is replaced now.
  private onWake = (): void => {
    if (!this.currentTeamId) return;
    if (this.open && Date.now() - this.lastHeardAt < STALE_AFTER_MS) return;
    this._reconnectNow();
  };

  private onVisibility = (): void => {
    if (document.visibilityState === 'visible') this.onWake();
  };

  private _reconnectNow(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.reconnectAttempts = 0;
    void this._connect(true);
  }

  private _closeStream(): void {
    this.stream?.abort();
    this.stream = null;
    this.open = false;
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  private _heard(): void {
    this.lastHeardAt = Date.now();
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    this.silenceTimer = setTimeout(() => {
      logger.warn('🔇 Conversation updates SSE went silent — reconnecting');
      this._reconnectNow();
    }, SILENT_LIMIT_MS);
  }

  /**
   * Open the stream. A reconnect always mints a fresh token: the one the page
   * started with may have expired while the laptop slept.
   */
  private async _connect(freshToken: boolean): Promise<void> {
    const teamId = this.currentTeamId;
    if (!teamId) return;
    this._closeStream();
    const stream = new AbortController();
    this.stream = stream;

    try {
      const token = freshToken ? await refreshUserToken() : await getAuthToken();
      if (this.stream !== stream) return;
      if (!token) throw new Error('no auth token yet');

      const { getApiBaseUrl } = await import('../utils/apiConfig');
      const url = `${getApiBaseUrl()}/conversations/updates/stream?teamId=${teamId}`;
      logger.info(`🔔 Connecting to conversation updates SSE: ${url}`);

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
          ...(this.viewingAs ? { 'X-Impersonate-User': this.viewingAs } : {}),
        },
        cache: 'no-store',
        signal: stream.signal,
      });
      if (res.status === 403) {
        logger.error('❌ Conversation updates SSE refused (403): not a member of this team');
        this._closeStream();
        return;
      }
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      logger.info('✅ Conversation updates SSE connection opened');
      this.open = true;
      this.reconnectAttempts = 0;
      this._heard();
      await this._read(res.body);
      throw new Error('stream ended');
    } catch (error) {
      if (this.stream !== stream) return;
      logger.error('❌ Conversation updates SSE error:', error);
      this._closeStream();
      this._scheduleReconnect();
    }
  }

  private async _read(body: ReadableStream<Uint8Array>): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      this._heard();
      buffer += decoder.decode(value, { stream: true });
      let end: RegExpExecArray | null;
      while ((end = /\r?\n\r?\n/.exec(buffer))) {
        this._dispatchFrame(buffer.slice(0, end.index));
        buffer = buffer.slice(end.index + end[0].length);
      }
    }
  }

  private _dispatchFrame(frame: string): void {
    const data = frame
      .split(/\r?\n/)
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).replace(/^ /, ''))
      .join('\n');
    if (!data) return;
    try {
      this._handleEvent(JSON.parse(data));
    } catch (error) {
      logger.error('❌ Error parsing conversation update event:', error);
    }
  }

  /**
   * Handle incoming SSE events
   */
  private _handleEvent(data: ConversationUpdateEvent): void {
    logger.debug('📨 Received conversation update event:', data);

    switch (data.type) {
      case 'connected':
        logger.info('🔗 Conversation updates SSE connected:', data.connectionId);
        break;

      case 'conversation_updated':
        if (data.conversationId && data.updates) {
          logger.info('📝 Conversation updated:', data.conversationId, data.updates);

          // Notify all callbacks
          this.callbacks.forEach(callback => {
            try {
              callback(data.conversationId!, data.updates!);
            } catch (error) {
              logger.error('❌ Error in conversation update callback:', error);
            }
          });

          // Check for audio generation started (from MCP generateTts/generateTtsAll)
          if (data.updates.audioGenerationStarted) {
            console.log('🎵 [AUDIO-STARTED] Received audioGenerationStarted via SSE:', {
              conversationId: data.conversationId,
              totalQueued: data.updates.totalQueued,
              outlineSlideId: data.updates.outlineSlideId,
            });

            window.dispatchEvent(new CustomEvent('narration-updated', {
              detail: {
                conversationId: data.conversationId,
                updateType: 'audio_generation_queued',
                timestamp: data.timestamp,
                source: 'mcp_sse',
              }
            }));
          }

          // Check for slide updates from worker and dispatch refresh event
          // This handles the race condition where worker sends slidesUpdated=true
          // AFTER messageController has already sent message_complete with slidesUpdated=false
          if (data.updates.slidesUpdated) {
            // Debug logging for slide refresh troubleshooting
            console.log('🎨 [SLIDE-REFRESH] Received slidesUpdated via SSE:', {
              conversationId: data.conversationId,
              timestamp: data.timestamp,
              currentSlideVersions: data.updates.currentSlideVersions,
            });
            logger.info('🎨 [SLIDE-REFRESH] Dispatching slides-updated CustomEvent');

            window.dispatchEvent(new CustomEvent('slides-updated', {
              detail: {
                conversationId: data.conversationId,
                currentSlideVersions: data.updates.currentSlideVersions || {},
                slideConversationId: data.updates.slideConversationId,
                outlineSlideId: data.updates.outlineSlideId,
                timestamp: data.timestamp,
                source: 'worker_sse'
              }
            }));
          }

          // Check for script-only updates from worker
          // This is dispatched when AI updates script.txt without changing slide.json
          if (data.updates.scriptUpdated) {
            console.log('📝 [SCRIPT-UPDATE] Received scriptUpdated via SSE:', {
              conversationId: data.conversationId,
              slideId: data.updates.slideId,
              timestamp: data.timestamp,
            });
            logger.info('📝 [SCRIPT-UPDATE] Dispatching script-updated CustomEvent');

            window.dispatchEvent(new CustomEvent('script-updated', {
              detail: {
                conversationId: data.conversationId,
                slideId: data.updates.slideId,
                scriptText: data.updates.scriptText,
                timestamp: data.timestamp,
                source: 'worker_sse'
              }
            }));
          }

          // Outline updates (from MCP tools or worker)
          if (data.updates.outlineUpdated) {
            console.log('📋 [OUTLINE-UPDATE] Received outlineUpdated via SSE:', {
              conversationId: data.conversationId,
              updateType: data.updates.updateType,
              timestamp: data.timestamp,
            });

            window.dispatchEvent(new CustomEvent('outline-updated', {
              detail: {
                conversationId: data.conversationId,
                updateType: data.updates.updateType,
                timestamp: data.timestamp,
                source: 'mcp_sse',
              }
            }));
          }

          // Assessment sync → live-update the assessment studio (mirror of slidesUpdated).
          if (data.updates.assessmentUpdated) {
            window.dispatchEvent(new CustomEvent('assessment-updated', {
              detail: {
                conversationId: data.conversationId,
                updateType: data.updates.updateType,
                timestamp: data.timestamp,
                source: 'mcp_sse',
              }
            }));
          }

          // Lab asset published/updated/archived → live-update the Lab tab.
          if (data.updates.labAssetsUpdated) {
            window.dispatchEvent(new CustomEvent('lab-assets-updated', {
              detail: {
                conversationId: data.conversationId,
                updateType: data.updates.updateType,
                labAssetId: data.updates.labAssetId,
                slug: data.updates.slug,
                timestamp: data.timestamp,
                source: 'mcp_sse',
              }
            }));
          }

          // Storyboard published (sync_storyboard) → the Storyboard tab reloads to the new version.
          if (data.updates.storyboardUpdated) {
            window.dispatchEvent(new CustomEvent('storyboard-updated', {
              detail: { conversationId: data.conversationId, version: data.updates.storyboardUpdated.version, timestamp: data.timestamp },
            }));
          }

          // A storyboard input was stored or changed status (pending → applied | superseded).
          if (data.updates.storyboardInput) {
            window.dispatchEvent(new CustomEvent('storyboard-input', {
              detail: {
                conversationId: data.conversationId,
                inputId: data.updates.storyboardInput.inputId,
                status: data.updates.storyboardInput.status,
                timestamp: data.timestamp,
              },
            }));
          }
          if (data.updates.sourceProgress) {
            window.dispatchEvent(new CustomEvent('source-progress', {
              detail: { conversationId: data.conversationId, ...data.updates.sourceProgress },
            }));
          }

          // Manifest built (from MCP tools)
          if (data.updates.manifestUpdated) {
            console.log('🎬 [MANIFEST-UPDATE] Received manifestUpdated via SSE:', {
              conversationId: data.conversationId,
              timestamp: data.timestamp,
            });

            window.dispatchEvent(new CustomEvent('manifest-updated', {
              detail: {
                conversationId: data.conversationId,
                timestamp: data.timestamp,
                source: 'mcp_sse',
              }
            }));
          }

          // Project created (from MCP tools)
          if (data.updates.projectCreated) {
            console.log('🆕 [PROJECT-CREATED] Received projectCreated via SSE:', {
              conversationId: data.conversationId,
              projectTitle: data.updates.projectTitle,
              timestamp: data.timestamp,
            });

            window.dispatchEvent(new CustomEvent('project-created', {
              detail: {
                conversationId: data.conversationId,
                projectTitle: data.updates.projectTitle,
                timestamp: data.timestamp,
                source: 'mcp_sse',
              }
            }));
          }

          // Check for message completion events from worker (for child slide conversations)
          // This notifies ChatArea when a message is complete, even if the per-message SSE isn't connected
          if (data.updates.messageCompleted) {
            console.log('📬 [MESSAGE-COMPLETE] Received messageCompleted via SSE:', {
              conversationId: data.conversationId,
              messageId: data.updates.messageId,
              timestamp: data.timestamp,
            });

            window.dispatchEvent(new CustomEvent('message-completed', {
              detail: {
                conversationId: data.conversationId,
                messageId: data.updates.messageId,
                timestamp: data.timestamp,
                source: 'worker_sse'
              }
            }));
          }

          // Check for audio events from worker
          if (data.updates.audioEvent) {
            const audioEvent = data.updates.audioEvent as AudioEvent;
            // Extract traceId for end-to-end tracing
            const traceId = (audioEvent as any).traceId || 'unknown';

            console.log(`🔊 [AUDIO-TRACE:${traceId}] Client received audio event via SSE`, {
              type: audioEvent.type,
              slideId: audioEvent.slideId,
              slideIdentifier: (audioEvent as any).slideIdentifier,
              status: audioEvent.status,
              progress: audioEvent.progress,
              conversationId: data.conversationId,
              callbackCount: this.audioCallbacks.size,
            });

            logger.info('🔊 [AUDIO-EVENT] Received audio event via SSE:', {
              type: audioEvent.type,
              slideId: audioEvent.slideId,
              status: audioEvent.status,
              progress: audioEvent.progress,
            });

            // Notify all audio callbacks
            this.audioCallbacks.forEach(callback => {
              try {
                callback(data.conversationId!, audioEvent);
              } catch (error) {
                logger.error('❌ Error in audio event callback:', error);
              }
            });

            console.log(`🔊 [AUDIO-TRACE:${traceId}] Dispatching audio-event CustomEvent to window`);

            // Also dispatch a custom event for components that prefer event-based listening
            window.dispatchEvent(new CustomEvent('audio-event', {
              detail: {
                conversationId: data.conversationId,
                audioEvent,
                timestamp: data.timestamp,
              }
            }));

            console.log(`🔊 [AUDIO-TRACE:${traceId}] CustomEvent dispatched successfully`);
          }

          // Check for batch TTS events from worker (audio-first flow)
          if (data.updates.batchTtsEvent) {
            console.log('🎵 [BATCH-TTS] Received batchTtsEvent via SSE:', {
              conversationId: data.conversationId,
              type: data.updates.batchTtsEvent.type,
              batchId: data.updates.batchTtsEvent.batchId,
            });

            window.dispatchEvent(new CustomEvent('narration-updated', {
              detail: {
                conversationId: data.conversationId,
                updateType: data.updates.updateType || 'batch_tts_completed',
                timestamp: data.timestamp,
                source: 'worker_sse',
              }
            }));
          }

          // Check for audio slicing events from worker (audio-first flow — master audio split per slide)
          if (data.updates.audioSlicingEvent) {
            console.log('✂️ [AUDIO-SLICING] Received audioSlicingEvent via SSE:', {
              conversationId: data.conversationId,
              type: data.updates.audioSlicingEvent.type,
            });

            window.dispatchEvent(new CustomEvent('narration-updated', {
              detail: {
                conversationId: data.conversationId,
                updateType: data.updates.updateType || 'audio_slicing_complete',
                timestamp: data.timestamp,
                source: 'worker_sse',
              }
            }));
          }

          // Check for audio-applied events (from apply_audio MCP tool)
          if (data.updates.audioApplied) {
            console.log('🔊 [AUDIO-APPLIED] Received audioApplied via SSE:', {
              conversationId: data.conversationId,
              slides: data.updates.slides,
            });

            window.dispatchEvent(new CustomEvent('narration-updated', {
              detail: {
                conversationId: data.conversationId,
                updateType: 'audio_applied',
                timestamp: data.timestamp,
                source: 'mcp_sse',
              }
            }));
          }

          // Check for audio split events (from split_batch_audio MCP tool)
          if (data.updates.audioSplitEvent) {
            console.log('✂️ [AUDIO-SPLIT] Received audioSplitEvent via SSE:', {
              conversationId: data.conversationId,
              type: data.updates.audioSplitEvent.type,
            });

            window.dispatchEvent(new CustomEvent('narration-updated', {
              detail: {
                conversationId: data.conversationId,
                updateType: data.updates.updateType || 'batch_audio_split',
                timestamp: data.timestamp,
                source: 'mcp_sse',
              }
            }));
          }

          // Check for transcription events from worker
          if (data.updates.transcriptionEvent) {
            const transcriptionEvent = data.updates.transcriptionEvent as TranscriptionEvent;

            logger.info('🎙️ [TRANSCRIPTION-EVENT] Received via SSE:', {
              type: transcriptionEvent.type,
              resourceId: transcriptionEvent.resourceId,
              wordCount: transcriptionEvent.wordCount,
            });

            // Notify all transcription callbacks
            this.transcriptionCallbacks.forEach(callback => {
              try {
                callback(data.conversationId!, transcriptionEvent);
              } catch (error) {
                logger.error('Error in transcription event callback:', error);
              }
            });

            // Also dispatch a custom event for components that prefer event-based listening
            window.dispatchEvent(new CustomEvent('transcription-event', {
              detail: {
                conversationId: data.conversationId,
                transcriptionEvent,
                timestamp: data.timestamp,
              }
            }));
          }
        }
        break;

      case 'heartbeat':
        logger.debug('💓 Conversation updates heartbeat received');
        break;

      case 'error':
        logger.error('❌ Conversation updates SSE error from server:', data.message);
        break;

      default:
        logger.debug('❓ Unknown conversation update event type:', data.type);
    }
  }

  /**
   * Back off 1 s, 2 s, 4 s … up to 30 s, and keep trying: the page must not
   * silently stop hearing updates after a long sleep.
   */
  private _scheduleReconnect(): void {
    if (!this.currentTeamId) return;
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    this.reconnectAttempts++;
    const delay = Math.min(1000 * 2 ** (this.reconnectAttempts - 1), MAX_RECONNECT_DELAY_MS);
    logger.info(`🔄 Reconnecting conversation updates SSE in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      void this._connect(true);
    }, delay);
  }
}

// Create singleton instance
export const conversationUpdatesService = new ConversationUpdatesService();
export default conversationUpdatesService;